import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Alert
} from '@mui/material';
import {
  Gavel,
  LocalHospital,
  Security,
  AccountBalance,
  Warning,
  CheckCircle,
  Cancel,
  Update,
  Email,
  Shield,
  Business,
  Policy
} from '@mui/icons-material';

const TermsOfService = () => {
  const lastUpdated = "December 2024";

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 4, mb: 3, textAlign: 'center', bgcolor: 'secondary.50' }}>
        <Gavel sx={{ fontSize: 64, color: 'secondary.main', mb: 2 }} />
        <Typography variant="h3" component="h1" gutterBottom color="secondary.main">
          Terms of Service
        </Typography>
        <Typography variant="h6" color="text.secondary">
          BD Dashboard - Healthcare CRM Platform
        </Typography>
        <Chip 
          label={`Last Updated: ${lastUpdated}`} 
          color="secondary" 
          variant="outlined" 
          sx={{ mt: 2 }}
        />
      </Paper>

      {/* Agreement */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalance color="primary" />
            Agreement to Terms
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
            By accessing and using BD Dashboard ("Service", "Platform", "we", "us", or "our"), 
            you ("User", "you", or "your") agree to be bound by these Terms of Service ("Terms"). 
            If you disagree with any part of these terms, then you may not access the Service.
          </Typography>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Healthcare Professionals Only:</strong> This platform is intended solely for 
              licensed healthcare professionals and authorized healthcare organizations. By using this 
              service, you represent that you are a qualified healthcare provider.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Service Description */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalHospital color="primary" />
            Service Description
          </Typography>

          <Typography variant="body1" paragraph>
            BD Dashboard is a healthcare Customer Relationship Management (CRM) platform that provides:
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText
                primary="Lead Management"
                secondary="Patient inquiry tracking, appointment scheduling, and follow-up management"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText
                primary="Team Collaboration"
                secondary="Multi-user access, role-based permissions, team hierarchy management"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText
                primary="Analytics & Reporting"
                secondary="Performance metrics, lead conversion tracking, business intelligence"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText
                primary="Communication Tools"
                secondary="Call management, follow-up reminders, patient communication history"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* User Accounts */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business color="primary" />
            User Accounts & Registration
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Account Creation Requirements
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><Security color="info" /></ListItemIcon>
              <ListItemText
                primary="Professional Verification"
                secondary="Valid healthcare license or authorized healthcare organization affiliation"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Security color="info" /></ListItemIcon>
              <ListItemText
                primary="Accurate Information"
                secondary="Provide true, accurate, current, and complete information during registration"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Security color="info" /></ListItemIcon>
              <ListItemText
                primary="Account Security"
                secondary="Maintain the security of your password and accept responsibility for all activities under your account"
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Account Responsibilities
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><Warning color="warning" /></ListItemIcon>
              <ListItemText
                primary="Confidentiality"
                secondary="Maintain confidentiality of all patient and user information accessed through the platform"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Warning color="warning" /></ListItemIcon>
              <ListItemText
                primary="Compliance"
                secondary="Ensure all activities comply with applicable healthcare laws and regulations"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Warning color="warning" /></ListItemIcon>
              <ListItemText
                primary="Authorized Use Only"
                secondary="Use the service only for legitimate healthcare business purposes"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Acceptable Use */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Policy color="primary" />
            Acceptable Use Policy
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2, color: 'success.main' }}>
            Permitted Uses
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText
                primary="Healthcare Operations"
                secondary="Managing patient leads, scheduling, and healthcare service delivery"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText
                primary="Team Management"
                secondary="Coordinating healthcare teams and managing workflow"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText
                primary="Business Analytics"
                secondary="Analyzing performance metrics for healthcare service improvement"
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3, color: 'error.main' }}>
            Prohibited Uses
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><Cancel color="error" /></ListItemIcon>
              <ListItemText
                primary="Unauthorized Access"
                secondary="Attempting to access accounts, data, or systems without authorization"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Cancel color="error" /></ListItemIcon>
              <ListItemText
                primary="Data Misuse"
                secondary="Using patient information for non-healthcare purposes or sharing without authorization"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Cancel color="error" /></ListItemIcon>
              <ListItemText
                primary="System Interference"
                secondary="Interfering with platform security features or attempting to disrupt service"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Cancel color="error" /></ListItemIcon>
              <ListItemText
                primary="Illegal Activities"
                secondary="Using the platform for any illegal purposes or activities"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Data & Privacy */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Shield color="primary" />
            Data Protection & Privacy
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Healthcare Data Compliance
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><LocalHospital color="success" /></ListItemIcon>
              <ListItemText
                primary="HIPAA Compliance"
                secondary="We maintain HIPAA-compliant safeguards for protected health information (PHI)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Security color="success" /></ListItemIcon>
              <ListItemText
                primary="Data Security"
                secondary="Industry-standard encryption, access controls, and security monitoring"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Policy color="success" /></ListItemIcon>
              <ListItemText
                primary="Privacy Policy"
                secondary="Detailed privacy practices outlined in our Privacy Policy"
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            User Data Responsibilities
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><Warning color="warning" /></ListItemIcon>
              <ListItemText
                primary="Data Accuracy"
                secondary="Ensure all data entered into the system is accurate and up-to-date"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Warning color="warning" /></ListItemIcon>
              <ListItemText
                primary="Access Control"
                secondary="Properly manage user access and permissions within your organization"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Service Availability */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Update color="primary" />
            Service Availability & Modifications
          </Typography>

          <Typography variant="body1" paragraph>
            We strive to maintain high service availability but cannot guarantee uninterrupted access:
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon><Update color="info" /></ListItemIcon>
              <ListItemText
                primary="Maintenance Windows"
                secondary="Scheduled maintenance may temporarily interrupt service with advance notice"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Security color="info" /></ListItemIcon>
              <ListItemText
                primary="Emergency Maintenance"
                secondary="Critical security updates may require immediate service interruption"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText
                primary="Service Improvements"
                secondary="We regularly update and improve platform features and performance"
              />
            </ListItem>
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              We aim for 99.9% uptime and will notify users of planned maintenance via email and 
              in-platform notifications.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Limitation of Liability */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalance color="primary" />
            Limitation of Liability
          </Typography>

          <Typography variant="body1" paragraph>
            To the fullest extent permitted by applicable law:
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon><Warning color="warning" /></ListItemIcon>
              <ListItemText
                primary="Service 'As Is'"
                secondary="The service is provided 'as is' without warranties of any kind"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Warning color="warning" /></ListItemIcon>
              <ListItemText
                primary="Professional Judgment"
                secondary="The platform is a tool to assist healthcare professionals; clinical decisions remain your responsibility"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Warning color="warning" /></ListItemIcon>
              <ListItemText
                primary="Data Backup"
                secondary="Users are responsible for maintaining their own data backups"
              />
            </ListItem>
          </List>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Medical Disclaimer:</strong> This platform is for administrative and CRM purposes only. 
              It is not intended for medical diagnosis, treatment, or clinical decision-making.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Termination */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Cancel color="primary" />
            Account Termination
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Termination by User
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircle color="info" /></ListItemIcon>
              <ListItemText
                primary="Account Cancellation"
                secondary="You may terminate your account at any time by contacting support"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Warning color="warning" /></ListItemIcon>
              <ListItemText
                primary="Data Retention"
                secondary="Some data may be retained for legal compliance and medical record requirements"
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Termination by BD Dashboard
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><Warning color="error" /></ListItemIcon>
              <ListItemText
                primary="Terms Violation"
                secondary="Accounts may be suspended or terminated for violations of these Terms"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Security color="error" /></ListItemIcon>
              <ListItemText
                primary="Security Concerns"
                secondary="Accounts may be suspended for security reasons or suspicious activity"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Contact & Legal */}
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Email color="primary" />
            Contact Information & Legal
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Questions or Concerns
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions about these Terms of Service, please contact us:
          </Typography>

          <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="body1">
              <strong>Email:</strong> bddashboardcrm@gmail.com
            </Typography>
            <Typography variant="body1">
              <strong>Subject Line:</strong> Terms of Service Inquiry
            </Typography>
            <Typography variant="body1">
              <strong>Response Time:</strong> Within 48 hours
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Changes to Terms
          </Typography>
          <Typography variant="body1" paragraph>
            We reserve the right to modify these Terms at any time. We will notify users of 
            significant changes via email and platform notifications. Continued use of the 
            service after changes constitutes acceptance of the new Terms.
          </Typography>

          <Typography variant="h6" gutterBottom>
            Governing Law
          </Typography>
          <Typography variant="body1" paragraph>
            These Terms shall be governed by and construed in accordance with applicable laws, 
            including healthcare regulations and data protection laws.
          </Typography>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Effective Date:</strong> These Terms of Service are effective as of {lastUpdated} 
              and supersede all previous versions.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TermsOfService;